import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  Plus,
  List,
  Image,
  MessageSquare,
  BarChart3,
  Users,
  Settings,
  ArrowLeft
} from 'lucide-react';

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Access Denied
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            You need admin privileges to access this page.
          </p>
        </div>
      </div>
    );
  }

  const adminActions = [
    {
      title: 'Add Channel',
      description: 'Add a new YouTube channel to track',
      icon: Plus,
      href: '/admin/add-channel',
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      title: 'Pending Channels',
      description: 'Review and approve pending channel submissions',
      icon: List,
      href: '/admin/pending-channels',
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      title: 'Thumbnail Matches',
      description: 'Manage thumbnail matching and series detection',
      icon: Image,
      href: '/admin/thumbnail-matches',
      color: 'bg-purple-500 hover:bg-purple-600'
    },
    {
      title: 'Series Suggestions',
      description: 'Review user suggestions for series names',
      icon: MessageSquare,
      href: '/admin/suggestions',
      color: 'bg-orange-500 hover:bg-orange-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to main site
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Admin Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage manhwarecap.com content and settings
          </p>
        </div>

        {/* Admin Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {adminActions.map((action) => (
            <Link
              key={action.href}
              to={action.href}
              className="block"
            >
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-4">
                  <div className={`p-3 rounded-lg ${action.color} text-white mr-4`}>
                    <action.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {action.title}
                    </h3>
                  </div>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {action.description}
                </p>
              </div>
            </Link>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="mt-12">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            Quick Stats
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <div className="flex items-center">
                <BarChart3 className="w-8 h-8 text-blue-500 mr-3" />
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">--</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Recaps</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <div className="flex items-center">
                <Users className="w-8 h-8 text-green-500 mr-3" />
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">--</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Active Channels</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <div className="flex items-center">
                <Settings className="w-8 h-8 text-purple-500 mr-3" />
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">--</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Pending Items</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;